import { useCallback, useEffect, useRef } from 'react';

const IDLE_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export function useIdleTimeout(
  onTimeout: () => void,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  isActive: boolean = false,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  onTimeoutRef.current = onTimeout;

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    if (!isActive) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    resetTimer();

    for (const event of IDLE_EVENTS) {
      document.addEventListener(event, resetTimer);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      for (const event of IDLE_EVENTS) {
        document.removeEventListener(event, resetTimer);
      }
    };
  }, [isActive, resetTimer]);
}
